"use client";

import { useEffect } from "react";
import { captureAppCrash } from "@/lib/posthog";

/**
 * Last resort: catches a crash in the root layout itself, which is the one place
 * [locale]/error.tsx cannot reach. It replaces the whole document, so the app's
 * stylesheet, fonts and translations are all unavailable here — hence the inline
 * brand colours and the hardcoded copy. Nothing in this file may import from the
 * app tree, or the crash that broke the layout breaks the screen reporting it.
 */

const COPY = {
  nl: {
    heading: "Er ging iets mis.",
    body: "We konden deze pagina niet laden. Probeer het opnieuw. Je ontwerp is bewaard.",
    retry: "Probeer opnieuw",
    home: "Naar de homepage",
    support: "Hulp nodig? Mail ons op",
  },
  de: {
    heading: "Etwas ist schiefgelaufen.",
    body: "Wir konnten diese Seite nicht laden. Bitte versuche es erneut. Dein Entwurf ist gespeichert.",
    retry: "Erneut versuchen",
    home: "Zur Startseite",
    support: "Brauchst du Hilfe? Schreib uns an",
  },
  en: {
    heading: "Something went wrong.",
    body: "We could not load this page. Please try again. Your design is saved.",
    retry: "Try again",
    home: "Go to the homepage",
    support: "Need help? Email us at",
  },
} as const;

type Locale = keyof typeof COPY;

function currentLocale(): Locale {
  if (typeof window === "undefined") return "nl";
  const match = window.location.pathname.match(/^\/(nl|de|en|uk)\b/);
  // /uk reads the English copy.
  return match?.[1] === "uk" ? "en" : (match?.[1] as Locale) ?? "nl";
}

function currentPrefix(): string {
  if (typeof window === "undefined") return "nl";
  return window.location.pathname.match(/^\/(nl|de|en|uk)\b/)?.[1] ?? "nl";
}

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const locale = currentLocale();
  const copy = COPY[locale];

  useEffect(() => {
    captureAppCrash(error, "global");
  }, [error]);

  return (
    <html lang={locale}>
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          backgroundColor: "#F4EFE3",
          color: "#1A1815",
          fontFamily: "ui-sans-serif, system-ui, -apple-system, Segoe UI, Helvetica, Arial, sans-serif",
        }}
      >
        <main style={{ margin: "0 auto", maxWidth: "34rem", padding: "4rem 1.5rem" }}>
          <h1 style={{ margin: 0, fontSize: "2rem", fontWeight: 500, letterSpacing: "-0.015em" }}>
            {copy.heading}
          </h1>
          <p style={{ marginTop: "1rem", fontSize: "1.0625rem", lineHeight: 1.6, color: "#5A544B" }}>
            {copy.body}
          </p>
          <div style={{ marginTop: "2rem", display: "flex", flexWrap: "wrap", alignItems: "center", gap: "1.5rem" }}>
            <button
              onClick={reset}
              style={{
                border: 0,
                cursor: "pointer",
                backgroundColor: "#B33A1F",
                color: "#F4EFE3",
                padding: "0.875rem 1.5rem",
                fontSize: "1rem",
                fontWeight: 500,
                fontFamily: "inherit",
              }}
              type="button"
            >
              {copy.retry}
            </button>
            <a href={`/${currentPrefix()}`} style={{ color: "#1A1815", textUnderlineOffset: "4px" }}>
              {copy.home}
            </a>
          </div>
          <p style={{ marginTop: "2.5rem", fontSize: "0.75rem", color: "#6E665A" }}>
            {copy.support}{" "}
            <a href="mailto:contact@vellumprintworks.com" style={{ color: "#6E665A" }}>
              contact@vellumprintworks.com
            </a>
            {error.digest ? ` (${error.digest})` : null}
          </p>
        </main>
      </body>
    </html>
  );
}
